import React from 'react'
import classnames from 'classnames'
import { KeypadItem, KeypadEntryStatus, ScreenNavigationParams, AppMetaData, RootStoreState, CouponCode, validateCode as validateCodeAction } from 'mytours-read-common'
import { CollectionDetailsState } from 'mytours-read-common/src/collectionlist/reducer'
import {
	IonGrid,
	IonRow,
	IonCol,
	IonList,
	IonItem,
	IonLabel,
	IonSpinner,
	IonThumbnail,
	IonInput,
	withIonLifeCycle,
} from '@ionic/react'
import Button from '../../../common/components/Button'

import strings from '../../../../strings'

import './keypad.less'
import { getDeviceUUID, getDirMode, getPlatform } from 'mobile_web/modules/platform'
import { GetCollectionsShowResponse, GetScreensShowResponse, RedeemCode } from 'mytours-read-api-typescript'
import { validateCollectionCode } from 'mobile_web/modules/lockapp/functions'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageSrcSet, generateImageUrl } from 'common_lib/utils'
import { useDispatch, useSelector } from 'react-redux'

interface OwnProps {
	keypadEntryStatus?: KeypadEntryStatus
	loadItemOnKeypadEntry: (keyNumber: number) => void
	items: KeypadItem[]
	cleanItems: () => void
	loading: boolean
	forwardedRef?: React.Ref<Keypad>
}

interface Props extends OwnProps {
	appMetaData: AppMetaData
	collections: Map<number, CollectionDetailsState>
	validateCode: (couponCode: CouponCode, linkedId: number) => Promise<boolean>
}

interface State {
	valueArr: number[]
	errorMessage: string
	keypadValue: number
}

class Keypad extends React.Component<Props, State> {
	state = {
		valueArr: [],
		errorMessage: '',
		keypadValue: -1,
	}

	getMaxLength = (): number => {
		const { appMetaData } = this.props
		if (!appMetaData) {
			return
		}
		const maxLength = appMetaData.keypad_digits
		return maxLength
	}

	componentDidMount = () => {
		const { cleanItems } = this.props
		cleanItems()
	}

	ionViewDidLeave() {
		this.cleanItems()
	}

	cleanItems = () => {
		const { cleanItems } = this.props

		cleanItems()
		this.setState({
			valueArr: [],
			keypadValue: -1,
		})
	}

	numberToString = (value: number): string => {
		const isNumber = typeof value === 'number' && !Number.isNaN(value)
		return (isNumber && value.toString()) || ''
	}

	renderButton = (value: number): JSX.Element => (
		<Button className="keypad-button" onClick={() => this.updateValue(value)} ariaLabel={this.numberToString(value)}>
			{value}
		</Button>
	)

	updateValue = (value: number): void => {
		const { valueArr } = this.state

		if (this.getMaxLength() <= valueArr.length) {
			return
		}

		const newValueArray = [...this.state.valueArr, value]

		this.setState({ valueArr: newValueArray, keypadValue: this.getValue(newValueArray) })
	}

	getValue = (valueArr?: number[]): number => {
		if (!valueArr) {
			valueArr = this.state.valueArr
		}

		const value = valueArr.length ? Number(valueArr.join('')) : -1
		return value
	}

	removeValue = (): void => {
		const valueArr = [...this.state.valueArr]
		valueArr.pop()

		this.setState({
			valueArr,
			keypadValue: this.getValue(valueArr),
		})
	}

	onSubmit = (): void => {
		this.props.loadItemOnKeypadEntry(this.state.keypadValue)
	}

	navigateToItem = async ({ id: itemId, title, version: itemVersion, subtype, collectionIds }: KeypadItem) => {
		const { collections } = this.props

		this.cleanItems()

		if (collectionIds && !(await this.hasAccessToAnyCollection(collectionIds))) {
			const parentCollection = collections.get(collectionIds[0])?.details
			if (!parentCollection) {
				return
			}

			NavigationService.navigateToItem({
				itemId: parentCollection.id,
				itemVersion: parentCollection.version,
				type: parentCollection.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionTour ? 'tour' : 'list',
				title,
			})
			return
		}

		const screenParams: ScreenNavigationParams = {
			itemId,
			title,
			itemVersion,
		}

		switch (subtype) {
			case GetScreensShowResponse.ScreenTypeEnum.Panorama:
				NavigationService.navigateToScreenPanorama(screenParams)
				break
			case GetScreensShowResponse.ScreenTypeEnum.Story:
				NavigationService.navigateToScreenStory(screenParams)
				break
			case GetScreensShowResponse.ScreenTypeEnum.Web:
				NavigationService.navigateToScreenWeb(screenParams)
				break
			case GetCollectionsShowResponse.SubtypeEnum.CollectionList:
			case GetCollectionsShowResponse.SubtypeEnum.CollectionMenu:
				NavigationService.navigateToItem({
					itemId,
					itemVersion,
					type: 'list',
					title,
				})
				break
			case GetCollectionsShowResponse.SubtypeEnum.CollectionTour:
				NavigationService.navigateToItem({
					itemId,
					itemVersion,
					type: 'tour',
					title,
				})
				break
		}
	}

	hasAccessToAnyCollection = async (collectionIds: number[]) => {
		const { collections, validateCode } = this.props

		for (const collectionId of collectionIds) {
			const collection = collections.get(collectionId)?.details
			if (!collection) {
				continue
			}

			if (collection.code_lock || (collection.pricing?.active && !!collection.pricing?.web_price?.amount)) {
				const isValid = await validateCollectionCode(collection, validateCode)
				if (isValid) {
					return true
				}
			} else {
				return true
			}
		}

		return false
	}

	renderItem = (item: KeypadItem): JSX.Element => {
		const { icon } = item

		return (
			<IonItem key={item.id} onClick={() => this.navigateToItem(item)}>
				<IonThumbnail className={!(icon && icon.file) && 'empty-image'} slot="start">
					{icon && icon.file && (
						<img
							src={generateImageUrl(icon.file, { imageSizePreset: ImageSizePreset.Thumbnail })}
							srcSet={generateImageSrcSet(icon.file, { imageSizePreset: ImageSizePreset.Thumbnail })}
							alt={item.title}
						/>
					)}
				</IonThumbnail>
				<IonLabel>{item.title}</IonLabel>
			</IonItem>
		)
	}

	renderResults = (): JSX.Element => {
		const { items, cleanItems } = this.props
		if (!items) {
			return undefined
		}

		const loading = this.props.loading || this.areCollectionsForKeypadItemsLoading()

		return (
			<>
				<IonGrid className="results-list section-group" dir={getDirMode()}>
					<h2 className={classnames('subheading results-subheading', getDirMode())}>{strings.results}</h2>
					{loading ? (
						<IonSpinner name="crescent" />
					) : (
						<IonList className={classnames('section-group-list', getDirMode())} lines="full">
							{items.map(this.renderItem)}
						</IonList>
					)}
				</IonGrid>
				{cleanItems && (
					<Button
						fill="clear"
						className="backspace-button"
						onClick={cleanItems}
						ariaLabel={strings.accessibility_go_back}
					>
						<i className="action-icon fal fa-long-arrow-left" />
					</Button>
				)}
			</>
		)
	}

	areCollectionsForKeypadItemsLoading = () => {
		const { items, collections } = this.props

		if (!items) {
			return true
		}

		for (const item of items) {
			for (const collectionId of (item.collectionIds || [])) {
				if (!collections.get(collectionId)?.details) {
					return true
				}
			}
		}

		return false
	}

	render() {
		const { keypadEntryStatus, items } = this.props
		const { valueArr, errorMessage } = this.state
		const errorAlert = keypadEntryStatus === KeypadEntryStatus.Failed ? strings.keypad_entry_error : errorMessage

		return (
			<div className="keypad-container">
				<IonGrid className="keypad-grid">
					<IonRow className="value-row">
						<IonCol size="auto">
							<IonItem className="value-item" lines="none">
								<IonInput role="presentation" className="value-input" value={valueArr.join('')} readonly autofocus />
							</IonItem>
						</IonCol>
					</IonRow>
					{items && items.length > 0 ? (
						this.renderResults()
					) : (
						<>
							{errorAlert && (
								<IonRow role="alert" className="error-row">
									{errorAlert}
								</IonRow>
							)}
							<IonRow>
								<IonCol>{this.renderButton(1)}</IonCol>
								<IonCol>{this.renderButton(2)}</IonCol>
								<IonCol>{this.renderButton(3)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>{this.renderButton(4)}</IonCol>
								<IonCol>{this.renderButton(5)}</IonCol>
								<IonCol>{this.renderButton(6)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>{this.renderButton(7)}</IonCol>
								<IonCol>{this.renderButton(8)}</IonCol>
								<IonCol>{this.renderButton(9)}</IonCol>
							</IonRow>
							<IonRow>
								<IonCol>
									<Button
										fill="clear"
										className="backspace-button"
										onClick={this.removeValue}
										ariaLabel={strings.accessibility_remove}
									>
										<i className="action-icon fal fa-backspace" />
									</Button>
								</IonCol>
								<IonCol>{this.renderButton(0)}</IonCol>
								<IonCol>
									<Button fill="clear" className="backspace-button" onClick={this.onSubmit} ariaLabel={strings.view_stop}>
										<i className="action-icon fal fa-long-arrow-right" />
									</Button>
								</IonCol>
							</IonRow>
						</>
					)}
				</IonGrid>
			</div>
		)
	}
}

function ConnectedKeypad(props: OwnProps) {
	const dispatch = useDispatch()
	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const collections = useSelector((state: RootStoreState) => state.collectionlist.collectionDetails)
	const validateCode = (couponCode: CouponCode, linkedId: number) => {
		const platform = getPlatform()
		const deviceUUID = getDeviceUUID()

		return new Promise<boolean>(res => {
			dispatch(
				validateCodeAction.started({
					linkedId,
					linkedType: RedeemCode.LinkedTypeEnum.Collection,
					devicePlatform: platform,
					deviceUdid: deviceUUID,
					couponCode: couponCode.code,
					expireAt: couponCode.expireAt,
					onComplete: res
				}),
			)
		})
	}

	return <Keypad
		appMetaData={appMetaData}
		collections={collections}
		validateCode={validateCode}
		{...props}
	/>
}

const ConnectedKeypadRefTaking = React.forwardRef((props: OwnProps, ref: React.Ref<Keypad>) => (
	<ConnectedKeypad {...props} forwardedRef={ref} />
))
ConnectedKeypadRefTaking.displayName = 'ConnectedKeypadRefTaking'

export default withIonLifeCycle(ConnectedKeypadRefTaking)
